<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { CONSENT_REQUEST_TYPE } from "@core/constants"
import { useAuthStore } from "@core/stores/auth.store"
import { useConfigStore } from "@core/stores/config.store"
import { formatAddress, formatIdSource } from "@core/helpers"
import type { CompanyType, OwnerOutput } from "@core/composables"
import type {
    ConsentRequestInvitationDto,
    SourceConsentSignBuilding,
    SourceConsentSignRequestProps,
    SourceConsentSignSource,
} from "@core/types/source/consent_request"

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<{
    /** Demande chargée (GET sign) */
    request: ConsentRequestInvitationDto
}>()

const emit = defineEmits<{
    done: []
}>()

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Signature du consentement",
            message:
                "{name} vous êtes sur le point de signer le consentement nécessaire pour autoriser l’accès aux données du compteur d’électricité numéro {idsource}{addressSuffix}",
            /** Aligné sur `component.form_source_pdl_append_on_invitation.success_title` (legacy). */
            success_title: "Consentement signé",
            /**
             * Aligné sur `component.form_source_pdl_append_on_invitation.success_message` (legacy).
             * `locationTail` = « situé au … » ou chaîne vide avant le point final.
             */
            success_message:
                "{name} vous venez de signer le consentement nécessaire pour autoriser l’accès aux données du compteur d’électricité numéro {idsource}{locationTail}. Vous pouvez quitter cette page.",
        },
    },
})

// ================================
// STORES
// ================================

const authStore = useAuthStore()
const configStore = useConfigStore()

// ================================
// COMPUTED
// ================================

/**
 * Titulaire / signataire (user_to) pour SourceConsentSign
 */
const getOwner = computed((): OwnerOutput => {
    const u = props.request.user_to
    return {
        user: {
            _id: u?._id ?? undefined,
            firstname: u?.firstname ?? "",
            lastname: u?.lastname ?? "",
            username: (u?.username ?? "").toLowerCase(),
        },
        company: u?.company?.name
            ? {
                  _id: u.company._id ?? "",
                  name: u.company.name,
                  type:
                      (u.company.type as CompanyType | undefined) ??
                      "entreprise",
                  address: null,
              }
            : null,
    }
})

const getSource = computed(
    (): SourceConsentSignSource => ({
        idsource: props.request.source?.idsource ?? "",
        idtype: props.request.source?.idtype ?? "pdl",
        date_from_data:
            props.request.consentement?.date_from_data ??
            props.request.consentement?.date_from ??
            undefined,
    })
)

const getBuilding = computed(
    (): SourceConsentSignBuilding => ({
        _id_building: props.request.building?._id ?? "",
        address: {
            street: props.request.address?.street ?? "",
            zipcode: props.request.address?.zipcode ?? "",
            city: props.request.address?.city ?? "",
            country: props.request.address?.country ?? "",
            citycode: props.request.address?.citycode ?? null,
        },
    })
)

/**
 * Durée d’affichage (mois) : préférence entreprise émettrice ou scope
 */
const getConsentDurationMonths = computed(() => {
    const fromOp = props.request.user_from?.company?.operation?.consent_duration
    if (fromOp != null) return fromOp
    const scopeDuration = (
        configStore.scope as { consentement?: { duration?: number } } | null
    )?.consentement?.duration
    if (scopeDuration != null) return scopeDuration / 12
    return 60
})

const getConsentRequestType = computed(() => {
    const ty = props.request.type
    if (ty === CONSENT_REQUEST_TYPE.CHANGE_OWNER)
        return CONSENT_REQUEST_TYPE.CHANGE_OWNER
    return CONSENT_REQUEST_TYPE.SIGN
})

const consentSignRequest = computed(
    (): SourceConsentSignRequestProps => ({
        _id_consentrequest: props.request._id,
        type: getConsentRequestType.value,
        mail: true,
        duration: getConsentDurationMonths.value,
    })
)

const getAddressText = computed(() =>
    props.request.address ? formatAddress(props.request.address) : ""
)

const signerName = computed(() => {
    const u = props.request.user_to
    return `${u?.firstname ?? ""} ${u?.lastname ?? ""}`.trim()
})

const formattedIdsource = computed(() =>
    props.request.source?.idsource
        ? formatIdSource(
              props.request.source.idsource,
              props.request.source.idtype ?? "pdl"
          )
        : ""
)

const headerMessage = computed(() => {
    const addressSuffix = getAddressText.value
        ? ` situé au ${getAddressText.value}.`
        : "."
    return t("message", {
        name: signerName.value,
        idsource: formattedIdsource.value,
        addressSuffix,
    })
})

/** Suite du message de succès après le numéro de PDL (legacy : « situé au … » sans point). */
const successLocationTail = computed(() =>
    getAddressText.value ? ` situé au ${getAddressText.value}` : ""
)

const successMessage = computed(() =>
    t("success_message", {
        name: signerName.value,
        idsource: formattedIdsource.value,
        locationTail: successLocationTail.value,
    })
)

const dateFrom = computed(() => {
    const d =
        props.request.consentement?.date_from_data ??
        props.request.consentement?.date_from
    if (d) return String(d).slice(0, 10)
    return new Date().toISOString().slice(0, 10)
})

// ================================
// STATE
// ================================

const displayHeaderMessage = ref(true)
/** True une fois la signature réussie : masque le formulaire et affiche le message de succès (legacy). */
const consentSigned = ref(false)

// ================================
// ACTIONS
// ================================

/**
 * Après signature : JWT éventuel, message de succès à la place du formulaire, puis événement `done`.
 */
async function onSigned(payload: {
    consentId?: string
    sourceId?: string
    token?: string
}): Promise<void> {
    if (payload.token) {
        authStore.setToken(payload.token)
        await authStore.fetchUser()
    }
    displayHeaderMessage.value = false
    consentSigned.value = true
    emit("done")
}

function onConsentBack(): void {
    displayHeaderMessage.value = true
}
</script>

<template>
    <div
        class="pdl-form-on-invitation flex flex-col items-center gap-8 w-full max-w-3xl mx-auto"
    >
        <header class="flex flex-col items-center gap-8 w-full pt-6">
            <h1 class="text-xl font-semibold text-gray-800 text-center">
                {{ consentSigned ? t("success_title") : t("title") }}
            </h1>
            <p
                v-if="consentSigned"
                class="text-sm text-gray-700 text-center max-w-2xl"
            >
                {{ successMessage }}
            </p>
            <p
                v-else-if="displayHeaderMessage"
                class="text-sm text-gray-700 text-center max-w-2xl"
            >
                {{ headerMessage }}
            </p>
        </header>
        <SourceConsentSign
            v-if="!consentSigned && request.source?.idsource && request._id"
            class="w-full"
            :owner="getOwner"
            :source="getSource"
            :building="getBuilding"
            :date-from="dateFrom"
            :request="consentSignRequest"
            @back="onConsentBack"
            @signed="onSigned"
            hide-header-title
            hide-back-button
        />
    </div>
</template>
